import { useCallback, useEffect, useState } from "react";

interface ImageCarouselProps {
  images: string[];
  alt: string;
  className?: string;
  autoPlay?: boolean;
  showDots?: boolean;
  showArrows?: boolean;
}

const ImageCarousel = ({
  images,
  alt,
  className = "",
  autoPlay = false,
  showDots = true,
  showArrows = true,
}: ImageCarouselProps) => {
  const [current, setCurrent] = useState(0);
  const allImages = images && images.length > 0 ? images : [];

  const prev = useCallback(() => {
    setCurrent((c) => (c === 0 ? allImages.length - 1 : c - 1));
  }, [allImages.length]);

  const next = useCallback(() => {
    setCurrent((c) => (c === allImages.length - 1 ? 0 : c + 1));
  }, [allImages.length]);

  useEffect(() => {
    if (!autoPlay || allImages.length <= 1) return;
    const timer = setInterval(next, 3500);
    return () => clearInterval(timer);
  }, [autoPlay, allImages.length, next]);

  if (allImages.length === 0) return null;

  if (allImages.length === 1) {
    return (
      <img
        src={allImages[0]}
        alt={alt}
        className={`w-full h-full object-contain ${className}`}
      />
    );
  }

  return (
    <div className={`relative overflow-hidden group/carousel ${className}`}>
      {/* Images */}
      <div
        className="flex h-full transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {allImages.map((src, idx) => (
          <div key={idx} className="w-full h-full flex-shrink-0">
            <img src={src} alt={`${alt} ${idx + 1}`} className="w-full h-full object-contain" />
          </div>
        ))}
      </div>

      {/* Arrows */}
      {showArrows && allImages.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 group-hover/carousel:opacity-100 transition-opacity cursor-pointer hover:bg-black/60 z-10"
          >
            <i className="ri-arrow-left-s-line text-sm" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 group-hover/carousel:opacity-100 transition-opacity cursor-pointer hover:bg-black/60 z-10"
          >
            <i className="ri-arrow-right-s-line text-sm" />
          </button>
        </>
      )}

      {/* Dots */}
      {showDots && allImages.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
          {allImages.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={(e) => { e.stopPropagation(); setCurrent(idx); }}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                idx === current
                  ? "w-4 h-1.5 bg-white"
                  : "w-1.5 h-1.5 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      )}

      {/* Image counter */}
      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/40 text-white text-[10px] font-medium z-10 opacity-0 group-hover/carousel:opacity-100 transition-opacity">
        {current + 1}/{allImages.length}
      </div>
    </div>
  );
};

export default ImageCarousel;
